import { Button, ButtonProps, ButtonTheme } from 'components/Button';
import { Icon, ICONS } from 'components/Icon';
import { Pressable } from 'components/Pressable';
import { ScreenOverlay } from 'components/ScreenOverlay';
import { Txt } from 'components/Txt';
import { useEsc } from 'hooks/useEsc';
import { theme } from 'lib/theme';
import { noop } from 'lodash';
import React, { ReactNode } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  TextStyle,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import { ActivityIndicator } from './BlockingLoader/ActivityIndicator';
import { ViewWithSeparators } from './ViewWithSeparators';

type DialogButtons = (ButtonProps & { initDisabled?: boolean })[];

interface Props {
  title?: string;
  titleIcon?: ICONS;
  titleIconColor?: string;
  subtitle?: string;
  text?: ReactNode | string;
  buttons?: DialogButtons;
  style?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
  onDismiss?: () => void;
  maxWidth?: number;
  width?: number;
  children?: React.ReactNode;
  scrollEnabled?: boolean;
  contentStyle?: StyleProp<ViewStyle>;
  dismissWithOverlay?: boolean;
}

export const Dialog: React.FC<Props> = ({
  buttons,
  children,
  maxWidth = 400,
  onDismiss,
  style,
  text,
  title,
  titleStyle,
  titleIcon,
  titleIconColor,
  subtitle,
  width,
  scrollEnabled = true,
  contentStyle,
  dismissWithOverlay = true,
}) => {
  const unblockTimeout = React.useRef<ReturnType<typeof setTimeout>>(null);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();

  const finalWidth = Math.round(
    Math.min(
      maxWidth,
      width || windowWidth,
      windowWidth - theme.paddingHorizontal * 4,
    ),
  );
  const maxHeight = windowHeight - theme.paddingHorizontal * 4;
  const marginHorizontal = (windowWidth - finalWidth) / 2;

  const [buttonBlocked, setButtonBlocked] = React.useState(
    !!buttons?.find((button) => button.initDisabled),
  );

  React.useEffect(() => {
    if (buttonBlocked) {
      unblockTimeout.current = setTimeout(() => setButtonBlocked(false), 2000);
    }
    return () => {
      if (unblockTimeout.current) {
        clearTimeout(unblockTimeout.current);
        unblockTimeout.current = null;
      }
    };
  }, [buttonBlocked]);

  useEsc(onDismiss);

  return (
    <ScreenOverlay>
      <View style={[StyleSheet.absoluteFill, styles.shim]} />
      <KeyboardAvoidingView
        behavior="height"
        style={[StyleSheet.absoluteFill, styles.keyboardAvoidingView]}
      >
        {dismissWithOverlay && (
          <Pressable
            onPress={onDismiss}
            style={[
              StyleSheet.absoluteFill,
              styles.pressable,
              onDismiss ? styles.pressableWithOnPress : undefined,
            ]}
          />
        )}
        <View
          focusable
          style={[
            styles.dialog,
            style,
            {
              marginHorizontal,
              maxHeight,
              width: finalWidth,
            },
          ]}
        >
          <ViewWithSeparators separatorMargin={2} style={styles.dialogInner}>
            <ScrollView
              scrollEnabled={scrollEnabled}
              contentContainerStyle={[
                { padding: theme.paddingVertical },
                contentStyle,
              ]}
            >
              <ViewWithSeparators
                separatorMargin={theme.paddingHorizontalHalf}
                separatorColor="transparent"
              >
                {titleIcon && (
                  <Icon
                    source={titleIcon}
                    size={32}
                    style={{ alignSelf: 'center' }}
                    color={titleIconColor}
                  />
                )}
                {(!!title || !!subtitle) && (
                  <>
                    {!!title && (
                      <Txt
                        weight="bold"
                        size="large"
                        style={[{ textAlign: 'center' }, titleStyle]}
                      >
                        {title}
                      </Txt>
                    )}
                    {!!subtitle && (
                      <Txt
                        weight="bold"
                        size="xxxsmall"
                        style={[{ textAlign: 'center' }, titleStyle]}
                      >
                        {subtitle}
                      </Txt>
                    )}
                  </>
                )}
                {text ? (
                  <Txt size="normal" style={{ textAlign: 'center' }}>
                    {text}
                  </Txt>
                ) : (
                  children || null
                )}
              </ViewWithSeparators>
            </ScrollView>
            {buttons && (
              <ViewWithSeparators
                horizontal={buttons.length < 3}
                separatorMargin={2}
                style={styles.buttons}
              >
                {(buttons.length < 3 ? buttons : [...buttons].reverse()).map(
                  (button, index) => (
                    <View
                      key={button.text}
                      style={buttons.length < 3 ? { flex: 1 } : undefined}
                    >
                      {button.initDisabled && buttonBlocked && (
                        <View style={styles.buttonBlocker}>
                          <ActivityIndicator
                            color={
                              button.destructive
                                ? theme.colors.red
                                : theme.colors.blue
                            }
                          />
                        </View>
                      )}
                      <Button
                        {...button}
                        key={button.text}
                        onPress={
                          button.initDisabled && buttonBlocked
                            ? noop
                            : button.onPress
                        }
                        focusable
                        focusOnRender={
                          buttons.length > 2
                            ? index === 0
                            : buttons.length === index + 1
                        }
                        style={[button.style, styles.button]}
                        styleHovered={{ borderColor: '#ddd' }}
                        textStyle={[styles.buttonText, button.textStyle]}
                        buttonTheme={
                          button.buttonTheme || ButtonTheme.secondary
                        }
                        adjustsFontSizeToFit
                      />
                    </View>
                  ),
                )}
              </ViewWithSeparators>
            )}
          </ViewWithSeparators>
        </View>
      </KeyboardAvoidingView>
    </ScreenOverlay>
  );
};

const styles = StyleSheet.create({
  buttons: {
    overflow: 'hidden',
    padding: 0,
  },
  button: {
    borderWidth: 0,
    borderRadius: 0,
    height: theme.grid * 4,
    paddingHorizontal: theme.paddingVertical,
    alignContent: 'center',
    justifyContent: 'center',
  },
  buttonBlocker: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
    backgroundColor: theme.colors.white50,
    zIndex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    textAlign: 'center',
  },
  dialog: {
    padding: 2,
    backgroundColor: 'white',
    borderRadius: 8,
    flexDirection: 'column',
    shadowOffset: { height: 0, width: 0 },
    shadowOpacity: 0.5,
    shadowRadius: 100,
    elevation: 10,
    // @ts-expect-error cursor is available on web
    cursor: 'undefined',
  },
  dialogInner: {
    borderRadius: 6,
    overflow: 'hidden',
  },
  keyboardAvoidingView: {
    justifyContent: 'center',
  },
  pressable: {
    flex: 1,
    justifyContent: 'center',
    position: 'absolute',
  },
  pressableWithOnPress: {
    flex: 1,
    ...Platform.select({
      web: { cursor: 'pointer' },
    }),
  },
  shim: {
    backgroundColor: theme.colors.backdrop,
    elevation: 9,
  },
});
